import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import axios from 'axios';
import {
  Box, Typography, Paper, Grid, FormControl, InputLabel, Select,
  MenuItem, ListSubheader, TextField, Button, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Chip, IconButton, Collapse,
  Stack, Divider, Alert, CircularProgress, Snackbar, Tooltip,
  InputAdornment, Accordion, AccordionSummary, AccordionDetails, LinearProgress,
} from '@mui/material';
import {
  Search as SearchIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  HowToReg as HowToRegIcon,
  Schedule as ScheduleIcon,
  WorkOutline as WorkOutlineIcon,
  Assignment as AssignmentIcon,
  Timer as TimerIcon,
  ContentCopy as ContentCopyIcon,
  EventNote as EventNoteIcon,
  Inventory2 as Inventory2Icon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { alpha, useTheme } from '@mui/material/styles';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const touchFriendlyReviewSx = {
  WebkitTapHighlightColor: 'transparent',
  touchAction: 'manipulation',
  '@media (pointer: coarse)': {
    '& .MuiButton-root': {
      minHeight: 48,
      borderRadius: '12px',
      fontSize: 14,
    },
    '& .MuiIconButton-root': {
      minWidth: 48,
      minHeight: 48,
      borderRadius: '12px',
    },
    '& .MuiInputBase-root': {
      minHeight: 48,
      fontSize: 14,
    },
    '& .MuiInputLabel-root': {
      fontSize: 14,
    },
    '& .MuiMenuItem-root': {
      minHeight: 48,
      fontSize: 14,
    },
    '& .MuiTableCell-root': {
      py: 1.4,
      fontSize: 13.5,
    },
    '& .MuiChip-root': {
      minHeight: 30,
      borderRadius: '10px',
    },
    '& .MuiAccordionSummary-root': {
      minHeight: 58,
    },
  },
};

/* grouped-select subheader — muiSkipListHighlight prevents keyboard nav stopping on it */
const MyListSubheader = (props) => <ListSubheader {...props} />;
MyListSubheader.muiSkipListHighlight = true;

const buildGroupsFromAllocatedMachines = (machines = []) => {
  const map = {};

  machines.forEach((machine) => {
    const code = machine.MachineCode || machine.code;
    if (!code) return;

    const groupCode = machine.MachineGroupCode || machine.groupCode || 'OTHER';
    const groupDescription = machine.GroupDescription || machine.groupDescription || groupCode;

    if (!map[groupCode]) {
      map[groupCode] = {
        groupCode,
        groupDescription,
        machines: [],
      };
    }

    map[groupCode].machines.push({
      code,
      description: (machine.MachineDescription || machine.description || code).trim(),
    });
  });

  return Object.values(map).map((group) => ({
    ...group,
    machines: group.machines.sort((a, b) => String(a.code).localeCompare(String(b.code))),
  }));
};



/* ─── helpers ───────────────────────────────────────────────────────────────── */
const fmt = (dt) => {
  if (!dt) return '—';
  return new Date(dt).toLocaleString('en-GB', {
    day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
  });
};

const fmtDur = (minutes) => {
  if (minutes == null || minutes === '') return '—';
  const m = parseFloat(minutes);
  if (isNaN(m)) return '—';
  const h = Math.floor(m / 60);
  const min = Math.round(m % 60);
  return h > 0 ? `${h}h ${min}m` : `${min}m`;
};

/* time-only formatter for timeline labels */
const fmtHHMM = (dt) => {
  if (!dt) return '';
  const d = new Date(dt);
  return d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
};

/* safe string match — avoids timezone/millisecond mismatches */
const normalise = (dt) => {
  if (!dt) return '';
  return String(dt).replace('T', ' ').substring(0, 19);
};

const typeStyle = (type, theme) => {
  const fallbackTheme = theme || {
    palette: {
      mode: 'light',
      success: { main: '#2e7d32', dark: '#1b5e20', light: '#81c784' },
      warning: { main: '#ed6c02', dark: '#e65100', light: '#ffb74d' },
      error: { main: '#d32f2f', dark: '#b71c1c', light: '#e57373' },
      primary: { main: '#1976d2', dark: '#0d47a1', light: '#64b5f6' },
      info: { main: '#0288d1', dark: '#01579b', light: '#4fc3f7' },
      secondary: { main: '#9c27b0', dark: '#6a1b9a', light: '#ba68c8' },
      text: { primary: '#111827', secondary: '#64748b' },
    },
  };

  const isDark = fallbackTheme.palette.mode === 'dark';

  const make = (paletteKey) => ({
    bg: alpha(fallbackTheme.palette[paletteKey].main, isDark ? 0.22 : 0.12),
    color: isDark
      ? fallbackTheme.palette[paletteKey].light
      : fallbackTheme.palette[paletteKey].dark,
    border: alpha(fallbackTheme.palette[paletteKey].main, isDark ? 0.65 : 0.45),
  });

  const styles = {
    Production: make('success'),
    Setup: make('warning'),
    Idle: make('error'),
    Meals: make('info'),
    JOB_STARTED: make('primary'),
    JOB_END: make('primary'),
    General: make('secondary'),
  };

  return styles[type] || {
    bg: alpha(fallbackTheme.palette.text.secondary, isDark ? 0.14 : 0.08),
    color: fallbackTheme.palette.text.secondary,
    border: alpha(fallbackTheme.palette.text.secondary, 0.35),
  };
};


/* ─── CopyCell: value + copy icon, touch + mouse responsive ────────────────── */
const CopyCell = ({ value, mono, color }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = (e) => {
    e.stopPropagation();
    if (!value) return;
    navigator.clipboard.writeText(value).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    }).catch(() => {
      /* fallback for older browsers / http */
      const el = document.createElement('textarea');
      el.value = value;
      el.style.position = 'fixed';
      el.style.opacity = '0';
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };

  return (
    <Stack
      direction="row" alignItems="center" spacing={0.5}
      sx={{
        /* mouse: show icon on row hover */
        '@media (hover: hover)': {
          '& .copy-btn': { opacity: 0 },
          '&:hover .copy-btn': { opacity: 1 },
        },
        /* touch: always show icon */
        '@media (hover: none)': {
          '& .copy-btn': { opacity: 1 },
        },
      }}
    >
      <Typography sx={{
        fontSize: 12, fontWeight: mono ? 600 : 400,
        color: color || 'text.primary',
        fontFamily: mono ? 'monospace' : 'inherit',
        whiteSpace: 'nowrap', overflow: 'hidden',
        textOverflow: 'ellipsis', maxWidth: mono ? 120 : 150,
      }}>
        {value || '—'}
      </Typography>
      <Tooltip
        title={copied ? 'Copied!' : `Copy ${value || ''}`}
        placement="top" arrow
        /* disable tooltip on touch — it blocks the tap */
        disableFocusListener={false}
        disableHoverListener={false}
        disableTouchListener={true}
      >
        <IconButton
          className="copy-btn"
          size="small"
          onClick={handleCopy}
          sx={{
            transition: 'opacity .15s, color .15s',
            /* larger tap target on touch */
            p: { xs: 0.8, sm: 0.2 },
            flexShrink: 0,
            color: copied ? 'success.main' : 'text.disabled',
            '&:hover': {
              color: copied ? 'success.main' : 'primary.main',
              bgcolor: 'transparent',
            },
            '&:active': {
              color: 'primary.main',
              transform: 'scale(0.9)',
            },
          }}
        >
          <ContentCopyIcon sx={{ fontSize: { xs: 14, sm: 11 } }} />
        </IconButton>
      </Tooltip>
    </Stack>
  );
};




/* ─── JobGroup: one job session card ────────────────────────────────────────── */
const JobGroup = ({
  group,
  machineCode,
  supervisorId,
  onSnackbar,
  expanded,
  onToggle,
  updateUrl,
  editSource,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const { jobStartedTime, opLogs, summary } = group;

  const softPrimaryBg = alpha(theme.palette.primary.main, isDark ? 0.16 : 0.06);
  const softSuccessBg = alpha(theme.palette.success.main, isDark ? 0.16 : 0.08);
  const softWarningBg = alpha(theme.palette.warning.main, isDark ? 0.18 : 0.10);
  const softErrorBg = alpha(theme.palette.error.main, isDark ? 0.18 : 0.08);
  const softInfoBg = alpha(theme.palette.info.main, isDark ? 0.16 : 0.08);
  const subtleBg = alpha(theme.palette.text.secondary, isDark ? 0.08 : 0.04);
  const hoverBg = alpha(theme.palette.primary.main, isDark ? 0.12 : 0.04);
  const timelineLineBg = alpha(theme.palette.divider, isDark ? 0.9 : 0.65);

  const EDIT_TIMEOUT = 120; // 2 minutes in seconds

  const [editing,    setEditing]    = useState(false);
  const [saving,     setSaving]     = useState(false);
  const [timeLeft,   setTimeLeft]   = useState(EDIT_TIMEOUT);
  const timerRef = useRef(null);

  const initDraft = useCallback(() => ({
    jobNo:         summary?.jobNo         || '',
    customer:      summary?.customer      || '',
    product:       summary?.product       || '',
    mainOperation: summary?.mainOperation || '',
    productionQty: summary?.productionQty ?? '',
    wasteQty:      summary?.wasteQty      ?? '',
  }), [summary]);

  const [draft, setDraft] = useState(initDraft);
  const [saved, setSaved] = useState(initDraft);

  const hasEdits = JSON.stringify(draft) !== JSON.stringify(saved);

  const clearTimer = useCallback(() => {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
  }, []);

  const startTimer = useCallback(() => {
    clearTimer();
    setTimeLeft(EDIT_TIMEOUT);
    timerRef.current = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          timerRef.current = null;
          setEditing(false);
          setDraft(initDraft());
          return EDIT_TIMEOUT;
        }
        return prev - 1;
      });
    }, 1000);
  }, [clearTimer, initDraft]);

  /* clean up timer on unmount */
  useEffect(() => () => clearTimer(), [clearTimer]);

  const openEdit = () => { setEditing(true); startTimer(); };

  const handleSave = async () => {
    if (!summary) return;
    setSaving(true);
    try {
      await axios.put(updateUrl, {
        employeeId:    summary.Employee_ID,
        jobNo:         saved.jobNo,
        newJobNo:      draft.jobNo,
        customer:      draft.customer,
        product:       draft.product,
        mainOperation: draft.mainOperation,
        productionQty: draft.productionQty,
        wasteQty:      draft.wasteQty,
        machineCode,
        supervisorId,
        startDatetime: jobStartedTime,  // raw MySQL string — no timezone conversion
        editSource,
      });
      setSaved({ ...draft });
      setEditing(false);
      clearTimer();
      setTimeLeft(EDIT_TIMEOUT);
      onSnackbar('Job details updated successfully', 'success');
    } catch (error) {
      onSnackbar(error.response?.data?.error || 'Failed to save changes', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setDraft({ ...saved });
    setEditing(false);
    clearTimer();
    setTimeLeft(EDIT_TIMEOUT);
  };

  /* timer color */
  const timerColor = timeLeft <= 30 ? 'error' : timeLeft <= 60 ? 'warning' : 'primary';
  const timerPct   = (timeLeft / EDIT_TIMEOUT) * 100;
  const fmtTime    = s => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

  const cardBorder = hasEdits ? '1.5px solid' : '1px solid';
  const cardBorderColor = hasEdits ? 'warning.light' : 'divider';
  const cardBg     = hasEdits ? softWarningBg : theme.palette.background.paper;

  return (
    <Paper elevation={0} sx={{
      border: cardBorder, borderColor: cardBorderColor, bgcolor: cardBg,
      borderRadius: 2,
      transition: 'border-color .2s, background-color .2s',
    }}>

      {/* ── header ── */}
      <Box
        sx={{
          px: 2.5, py: 1.8, display: 'flex', alignItems: 'flex-start',
          gap: 2, cursor: 'pointer',
          '&:hover': { bgcolor: hoverBg },
          transition: 'background .12s',
        }}
        onClick={() => onToggle()}
      >
        {/* job info */}
        <Box flex={1} minWidth={0}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" mb={0.6}>
            <Typography sx={{
              fontSize: 14, fontWeight: 700, color: 'text.primary',
              fontFamily: '"DM Mono", "Fira Code", monospace', letterSpacing: '.5px',
            }}>
              {draft.jobNo || <span style={{ color: 'text.secondary', fontStyle: 'italic' }}>No job number</span>}
            </Typography>
            {hasEdits && (
              <Chip label="Unsaved edits" size="small" sx={{
                height: 20, fontSize: 10.5, bgcolor: softWarningBg,
                color: theme.palette.warning.main, border: `1px solid ${alpha(theme.palette.warning.main, 0.45)}`, fontWeight: 600,
              }} />
            )}
          </Stack>
          <Stack direction="row" spacing={2} flexWrap="wrap">
            {[
              { label: 'Customer',  val: draft.customer      },
              { label: 'Product',   val: draft.product       },
              { label: 'Operation', val: draft.mainOperation },
            ].map(({ label, val }) => (
              <Box key={label}>
                <Typography sx={{ fontSize: 10, color: 'text.secondary', lineHeight: 1,
                  textTransform: 'uppercase', letterSpacing: '.4px' }}>
                  {label}
                </Typography>
                <Typography sx={{ fontSize: 12.5, color: 'text.primary', fontWeight: 500, mt: 0.2 }}>
                  {val || '—'}
                </Typography>
              </Box>
            ))}
          </Stack>
        </Box>

        {/* qty + log count */}
        <Stack direction="row" spacing={3} alignItems="center" flexShrink={0}>
          {[
            { label: 'Prod Qty',  val: draft.productionQty ?? '—', color: 'success.main' },
            { label: 'Waste Qty', val: draft.wasteQty      ?? '—', color: 'error.main'   },
            { label: 'Logs',      val: opLogs.length,               color: 'primary.main'  },
          ].map(({ label, val, color }) => (
            <Box key={label} textAlign="center">
              <Typography sx={{ fontSize: 10, color: 'text.secondary',
                textTransform: 'uppercase', letterSpacing: '.4px' }}>
                {label}
              </Typography>
              <Typography sx={{ fontSize: 15, fontWeight: 700, color,
                fontFamily: '"DM Mono", monospace' }}>
                {val}
              </Typography>
            </Box>
          ))}
        </Stack>

        {/* edit actions */}
        <Stack direction="row" spacing={0.75} alignItems="center" flexShrink={0}>
          <Box onClick={e => e.stopPropagation()} sx={{ display: 'flex', gap: 0.75 }}>
            {editing ? (
              <>
                <Tooltip title="Save changes">
                  <IconButton
                    aria-label="Save job edit changes"
                    onClick={handleSave}
                    disabled={saving}
                    sx={{
                      width: { xs: 48, md: 44 },
                      height: { xs: 48, md: 44 },
                      minWidth: { xs: 48, md: 44 },
                      minHeight: { xs: 48, md: 44 },
                      color: 'success.main',
                      bgcolor: softSuccessBg,
                      border: '1px solid',
                      borderColor: alpha(theme.palette.success.main, 0.45),
                      borderRadius: { xs: '12px', md: '10px' },
                      boxShadow: isDark ? 'none' : `0 2px 8px ${alpha(theme.palette.success.main, 0.16)}`,
                      transition: 'all .15s ease',
                      '&:hover': {
                        bgcolor: alpha(theme.palette.success.main, isDark ? 0.28 : 0.16),
                        borderColor: alpha(theme.palette.success.main, 0.65),
                      },
                      '&:active': {
                        transform: 'scale(0.96)',
                      },
                      '&.Mui-disabled': {
                        bgcolor: alpha(theme.palette.success.main, isDark ? 0.10 : 0.06),
                        borderColor: alpha(theme.palette.success.main, 0.20),
                      },
                    }}
                  >
                    {saving ? (
                      <CircularProgress size={20} color="inherit" />
                    ) : (
                      <SaveIcon sx={{ fontSize: { xs: 24, md: 22 } }} />
                    )}
                  </IconButton>
                </Tooltip>
                <Tooltip title="Cancel edit">
                  <IconButton
                    aria-label="Cancel job edit changes"
                    onClick={handleCancel}
                    sx={{
                      width: { xs: 48, md: 44 },
                      height: { xs: 48, md: 44 },
                      minWidth: { xs: 48, md: 44 },
                      minHeight: { xs: 48, md: 44 },
                      color: 'text.secondary',
                      bgcolor: 'background.paper',
                      border: '1px solid',
                      borderColor: alpha(theme.palette.text.secondary, isDark ? 0.32 : 0.22),
                      borderRadius: { xs: '12px', md: '10px' },
                      transition: 'all .15s ease',
                      '&:hover': {
                        bgcolor: 'action.hover',
                        color: 'error.main',
                        borderColor: alpha(theme.palette.error.main, 0.45),
                      },
                      '&:active': {
                        transform: 'scale(0.96)',
                      },
                    }}
                  >
                    <CancelIcon sx={{ fontSize: { xs: 24, md: 22 } }} />
                  </IconButton>
                </Tooltip>
              </>
            ) : (
              <Tooltip title="Edit job details">
                <IconButton
                  aria-label="Edit job details"
                  onClick={() => openEdit()}
                  sx={{
                    width: { xs: 48, md: 44 },
                    height: { xs: 48, md: 44 },
                    minWidth: { xs: 48, md: 44 },
                    minHeight: { xs: 48, md: 44 },
                    color: 'primary.main',
                    bgcolor: softPrimaryBg,
                    border: '1px solid',
                    borderColor: alpha(theme.palette.primary.main, 0.40),
                    borderRadius: { xs: '12px', md: '10px' },
                    transition: 'all .15s ease',
                    '&:hover': {
                      bgcolor: alpha(theme.palette.primary.main, isDark ? 0.26 : 0.12),
                      borderColor: alpha(theme.palette.primary.main, 0.62),
                    },
                    '&:active': {
                      transform: 'scale(0.96)',
                    },
                  }}
                >
                  <EditIcon sx={{ fontSize: { xs: 24, md: 22 } }} />
                </IconButton>
              </Tooltip>
            )}
          </Box>
          <IconButton
            aria-label={expanded ? 'Collapse job details' : 'Expand job details'}
            sx={{
              width: { xs: 48, md: 42 },
              height: { xs: 48, md: 42 },
              color: 'text.secondary',
              borderRadius: { xs: '12px', md: '10px' },
              '&:hover': { bgcolor: 'action.hover' },
              '&:active': { transform: 'scale(0.96)' },
            }}
            onClick={e => { e.stopPropagation(); onToggle(); }}
          >
            {expanded ? (
              <ExpandLessIcon sx={{ fontSize: { xs: 26, md: 22 } }} />
            ) : (
              <ExpandMoreIcon sx={{ fontSize: { xs: 26, md: 22 } }} />
            )}
          </IconButton>
        </Stack>
      </Box>

      {/* ── inline edit form ── */}
      <Collapse in={editing}>
        <Divider />
        {/* timer progress bar */}
        <LinearProgress
          variant="determinate" value={timerPct} color={timerColor}
          sx={{ height: 3, borderRadius: 0 }}
        />
        <Box sx={{ px: 2.5, py: 2, bgcolor: subtleBg }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1.5}>
            <Typography sx={{ fontSize: 11, fontWeight: 700, color: 'primary.main',
              textTransform: 'uppercase', letterSpacing: '.6px' }}>
              Edit Job Details
            </Typography>
            <Stack direction="row" spacing={0.75} alignItems="center">
              <TimerIcon sx={{ fontSize: 14, color: `${timerColor}.main` }} />
              <Typography sx={{
                fontSize: 12, fontWeight: 700, fontFamily: 'monospace',
                color: `${timerColor}.main`,
                transition: 'color .3s',
              }}>
                {fmtTime(timeLeft)}
              </Typography>
              <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>
                {timeLeft <= 30 ? '— auto-closing soon' : timeLeft <= 60 ? '— less than 1 min' : '— editing window'}
              </Typography>
            </Stack>
          </Stack>
          <Grid container spacing={1.5}>
            {[
              { key: 'jobNo',         label: 'Job Number',     xs: 6, md: 3 },
              { key: 'customer',      label: 'Customer',       xs: 6, md: 3 },
              { key: 'product',       label: 'Product',        xs: 6, md: 3 },
              { key: 'mainOperation', label: 'Main Operation', xs: 6, md: 3 },
            ].map(({ key, label, xs, md }) => (
              <Grid item xs={xs} md={md} key={key}>
                <TextField
                  fullWidth size="small" label={label}
                  value={draft[key]}
                  onChange={e => setDraft(d => ({ ...d, [key]: e.target.value }))}
                  sx={{
                    '& .MuiOutlinedInput-root': { borderRadius: '6px', fontSize: 13 },
                    '& .MuiInputLabel-root': { fontSize: 13 },
                  }}
                />
              </Grid>
            ))}
            <Grid item xs={6} md={3}>
              <TextField
                fullWidth size="small" label="Production Qty" type="number"
                value={draft.productionQty}
                onChange={e => setDraft(d => ({ ...d, productionQty: e.target.value }))}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Inventory2Icon sx={{ fontSize: 15, color: 'success.main' }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': { borderRadius: '6px', fontSize: 13 },
                  '& .MuiInputLabel-root': { fontSize: 13 },
                }}
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <TextField
                fullWidth size="small" label="Waste Qty" type="number"
                value={draft.wasteQty}
                onChange={e => setDraft(d => ({ ...d, wasteQty: e.target.value }))}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <WarningIcon sx={{ fontSize: 15, color: 'warning.main' }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': { borderRadius: '6px', fontSize: 13 },
                  '& .MuiInputLabel-root': { fontSize: 13 },
                }}
              />
            </Grid>
          </Grid>
        </Box>
      </Collapse>

      {/* ── expanded: job summary + operation log ── */}
      <Collapse in={expanded}>
        <Divider />

        {/* job summary detail panel */}
        {summary && (
          <Box sx={{ px: 2.5, py: 2, bgcolor: subtleBg,
            borderBottom: '1px solid', borderColor: 'divider' }}>
            <Typography sx={{ fontSize: 11, fontWeight: 700, color: 'text.secondary',
              textTransform: 'uppercase', letterSpacing: '.5px', mb: 1.5 }}>
              Job Summary Details
            </Typography>
            <Grid container spacing={1.5}>
              {/* time totals */}
              {[
                { label: 'Production Time', val: summary.totalProductionTime || '—' },
                { label: 'Setup Time',      val: summary.totalSetupTime      || '—' },
                { label: 'Idle Time',       val: summary.totalIdleTime       || '—' },
                { label: 'Meals Time',      val: summary.totalMealsTime      || '—' },
              ].map(({ label, val }) => (
                <Grid item xs={6} sm={3} key={label}>
                  <Paper elevation={0} sx={{ p: 1.2, borderRadius: 1.5, textAlign: 'center',
                    border: '1px solid', borderColor: 'divider', bgcolor: 'background.paper' }}>
                    <Typography sx={{ fontSize: 10, color: 'text.secondary',
                      textTransform: 'uppercase', letterSpacing: '.4px' }}>{label}</Typography>
                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: 'text.primary',
                      fontFamily: 'monospace', mt: 0.3 }}>{val}</Typography>
                  </Paper>
                </Grid>
              ))}
              {/* qty fields */}
              {[
                { label: 'Input Qty',      val: summary.InputQty,      color: 'text.primary'   },
                { label: 'Pass Qty',       val: summary.PassQty,       color: 'text.primary'   },
                { label: 'Prod Qty',       val: summary.productionQty, color: 'success.main'   },
                { label: 'Waste Qty',      val: summary.wasteQty,      color: 'error.main'     },
                { label: 'OP Qty',         val: summary.OPQty,         color: 'text.secondary' },
                { label: 'Planned Qty',    val: summary.plannedQty,    color: 'text.secondary' },
              ].map(({ label, val, color }) => (
                <Grid item xs={4} sm={2} key={label}>
                  <Paper elevation={0} sx={{ p: 1.2, borderRadius: 1.5, textAlign: 'center',
                    border: '1px solid', borderColor: 'divider', bgcolor: 'background.paper' }}>
                    <Typography sx={{ fontSize: 10, color: 'text.secondary',
                      textTransform: 'uppercase', letterSpacing: '.4px' }}>{label}</Typography>
                    <Typography sx={{ fontSize: 13, fontWeight: 700, color, fontFamily: 'monospace', mt: 0.3 }}>
                      {val ?? '—'}
                    </Typography>
                  </Paper>
                </Grid>
              ))}
              {/* meta fields */}
              {[
                { label: 'Finished Good',  val: summary.finishedGood },
                { label: 'Status',         val: summary.status       },
                { label: 'Avg Prod Speed', val: summary.avg_prod_speed != null ? `${summary.avg_prod_speed}/hr` : '—' },
                { label: 'Avg OP Speed',   val: summary.avg_op_speed  != null ? `${summary.avg_op_speed}/hr`  : '—' },
                { label: 'Start',          val: fmt(summary.startDatetime) },
                { label: 'End',            val: fmt(summary.endDatetime)   },
              ].map(({ label, val }) => (
                <Grid item xs={6} sm={2} key={label}>
                  <Box>
                    <Typography sx={{ fontSize: 10, color: 'text.secondary',
                      textTransform: 'uppercase', letterSpacing: '.4px' }}>{label}</Typography>
                    <Typography sx={{ fontSize: 12.5, fontWeight: 500, color: 'text.primary', mt: 0.2 }}>
                      {val || '—'}
                    </Typography>
                  </Box>
                </Grid>
              ))}
              {summary.idleReasons && (
                <Grid item xs={12}>
                  <Typography sx={{ fontSize: 10, color: 'text.secondary',
                    textTransform: 'uppercase', letterSpacing: '.4px', mb: 0.3 }}>Idle Reasons</Typography>
                  <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>{summary.idleReasons}</Typography>
                </Grid>
              )}
              {summary.jobCloseremarks && (
                <Grid item xs={12}>
                  <Typography sx={{ fontSize: 10, color: 'text.secondary',
                    textTransform: 'uppercase', letterSpacing: '.4px', mb: 0.3 }}>Job Close Remarks</Typography>
                  <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>{summary.jobCloseremarks}</Typography>
                </Grid>
              )}
            </Grid>
          </Box>
        )}

        {/* ── operation log timeline ── */}
        <Box>
          {/* header */}
          <Box sx={{ px: 2.5, py: 1.2, bgcolor: subtleBg,
            borderBottom: '1px solid', borderColor: 'divider' }}>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
              <ScheduleIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
              <Typography sx={{ fontSize: 11, fontWeight: 700, color: 'text.secondary',
                textTransform: 'uppercase', letterSpacing: '.5px' }}>
                Operation Log
              </Typography>
              <Chip label={`${opLogs.length} ${opLogs.length === 1 ? 'entry' : 'entries'}`}
                size="small" sx={{ fontSize: 10.5, height: 18, fontWeight: 600 }} />
              {/* operation type summary chips */}
              {['JOB_STARTED','Production','Setup','Idle','Meals','JOB_END'].map(type => {
                const count = opLogs.filter(r => r.operation_type === type).length;
                if (!count) return null;
                const ts = typeStyle(type, theme);
                return (
                  <Chip key={type} label={`${type.replace('_',' ')} ×${count}`} size="small"
                    sx={{ fontSize: 10, height: 18, fontWeight: 600,
                      bgcolor: ts.bg, color: ts.color, border: `1px solid ${ts.border}` }} />
                );
              })}
              <Typography sx={{ fontSize: 11, color: 'text.secondary', ml: 'auto !important',
                fontFamily: 'monospace' }}>
                {fmtHHMM(jobStartedTime)}
              </Typography>
            </Stack>
          </Box>

          {opLogs.length === 0 ? (
            <Box sx={{ py: 3, textAlign: 'center' }}>
              <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
                No operation log entries found.
              </Typography>
            </Box>
          ) : (
            <Box sx={{ px: 2, py: 1.5 }}>
              {opLogs.map((row, i) => {
                const isLast = i === opLogs.length - 1;
                const ts     = typeStyle(row.operation_type, theme);
                const isJob  = row.operation_type === 'JOB_STARTED' || row.operation_type === 'JOB_END';
                const isProd = row.operation_type === 'Production';

                return (
                  <Box key={`${row.start_datetime}-${i}`}
                    sx={{ display: 'flex', alignItems: 'stretch' }}>
                    {/* time + dot + spine */}
                    <Box sx={{ display: 'flex', flexDirection: 'column',
                      alignItems: 'center', width: 52, flexShrink: 0, mr: 1.5 }}>
                      <Typography sx={{
                        fontSize: 10.5, fontWeight: isJob ? 700 : 500,
                        color: isJob ? 'primary.main' : 'text.disabled',
                        fontFamily: 'monospace', mt: 0.8, lineHeight: 1,
                      }}>
                        {fmtHHMM(row.start_datetime)}
                      </Typography>
                      <Box sx={{
                        width: isJob ? 12 : isProd ? 10 : 8,
                        height: isJob ? 12 : isProd ? 10 : 8,
                        borderRadius: '50%', mt: 0.4, flexShrink: 0,
                        bgcolor: ts.color,
                        border: `2px solid ${ts.border}`,
                        boxShadow: isJob ? `0 0 0 3px ${ts.bg}` : 'none',
                      }} />
                      {!isLast && (
                        <Box sx={{
                          width: 2, flex: 1,
                          bgcolor: isProd ? alpha(theme.palette.success.main, isDark ? 0.35 : 0.22) : timelineLineBg,
                          mt: 0.4,
                        }} />
                      )}
                    </Box>

                    {/* operation card */}
                    <Box sx={{ flex: 1, mb: isLast ? 0 : 0.75 }}>
                      <Box sx={{
                        border: '1px solid',
                        borderColor: isJob ? ts.border : 'divider',
                        borderRadius: 1.5,
                        px: 1.5, py: 0.8,
                        bgcolor: isJob ? ts.bg : isProd ? softSuccessBg : theme.palette.background.paper,
                        display: 'flex', alignItems: 'flex-start',
                        gap: 1.5, flexWrap: 'wrap',
                      }}>
                        {/* type chip */}
                        <Chip label={row.operation_type || '—'} size="small"
                          sx={{ bgcolor: ts.bg, color: ts.color,
                                border: `1px solid ${ts.border}`,
                                fontWeight: 700, fontSize: 10, height: 18,
                                flexShrink: 0 }} />

                        {/* description + code */}
                        <Box flex={1} minWidth={0}>
                          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                            {row.operation_description && (
                              <Typography sx={{ fontSize: 12, fontWeight: isProd ? 600 : 400,
                                color: isProd ? 'success.dark' : 'text.primary' }}>
                                {row.operation_description}
                              </Typography>
                            )}
                            {row.operation_code && (
                              <Chip label={row.operation_code} size="small" variant="outlined"
                                sx={{ fontSize: 10, height: 16 }} />
                            )}
                          </Stack>
                          {row.remarks && !isJob && (
                            <Typography sx={{ fontSize: 11, color: 'text.secondary', mt: 0.3 }}>
                              {row.remarks}
                            </Typography>
                          )}
                          {isJob && row.remarks && (
                            <Typography sx={{ fontSize: 11, color: ts.color, mt: 0.3,
                              fontStyle: 'italic' }}>
                              {row.remarks.length > 80
                                ? row.remarks.substring(0, 80) + '…'
                                : row.remarks}
                            </Typography>
                          )}
                        </Box>

                        {/* right side — start → end + duration */}
                        <Stack alignItems="flex-end" flexShrink={0} spacing={0.3}>
                          {/* start → end time range */}
                          <Stack direction="row" spacing={0.5} alignItems="center">
                            <Typography sx={{ fontSize: 10.5, color: 'text.disabled',
                              fontFamily: 'monospace' }}>
                              {fmtHHMM(row.start_datetime)}
                            </Typography>
                            {row.end_datetime && row.start_datetime !== row.end_datetime && (
                              <>
                                <Typography sx={{ fontSize: 10, color: 'text.disabled' }}>→</Typography>
                                <Typography sx={{ fontSize: 10.5, color: 'text.disabled',
                                  fontFamily: 'monospace' }}>
                                  {fmtHHMM(row.end_datetime)}
                                </Typography>
                              </>
                            )}
                          </Stack>
                          {/* duration chip */}
                          {row.duration_minutes > 0 && (
                            <Chip
                              label={fmtDur(row.duration_minutes)}
                              size="small"
                              sx={{
                                fontSize: 10.5, height: 18, fontWeight: 700,
                                bgcolor: isProd ? softSuccessBg :
                                         row.operation_type === 'Setup' ? softWarningBg :
                                         row.operation_type === 'Idle'  ? softErrorBg   :
                                         row.operation_type === 'Meals' ? softInfoBg    : subtleBg,
                                color:   isProd ? (isDark ? theme.palette.success.light : theme.palette.success.dark) :
                                         row.operation_type === 'Setup' ? (isDark ? theme.palette.warning.light : theme.palette.warning.dark) :
                                         row.operation_type === 'Idle'  ? (isDark ? theme.palette.error.light : theme.palette.error.dark)   :
                                         row.operation_type === 'Meals' ? (isDark ? theme.palette.info.light : theme.palette.info.dark)    : 'text.secondary',
                              }}
                            />
                          )}
                        </Stack>
                      </Box>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          )}
        </Box>
      </Collapse>
    </Paper>
  );
};


/* ─── main component ─────────────────────────────────────────────────────────── */
const MachineHourApproval = ({
  username,
  allocatedMachines = null,
  restrictedMode = false,
  title = 'Machine-Hour Job Review',
  subtitle = 'View operation logs · edit job summary details',
  updateUrl = `${API_BASE_URL}/api/update-job-summary`,
  editSource = 'HR_MACHINE_HOUR_APPROVAL',
  jobReviewUrls = null,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const softPrimaryBg = alpha(theme.palette.primary.main, isDark ? 0.16 : 0.06);
  const softSuccessBg = alpha(theme.palette.success.main, isDark ? 0.16 : 0.08);
  const subtleBg = alpha(theme.palette.text.secondary, isDark ? 0.08 : 0.04);
  const subtleBgStrong = alpha(theme.palette.text.secondary, isDark ? 0.14 : 0.08);
  const tableHeaderBg = alpha(theme.palette.primary.main, isDark ? 0.22 : 0.10);
  const timelineLineBg = alpha(theme.palette.divider, isDark ? 0.9 : 0.65);

  const [machineGroups, setMachineGroups] = useState([]);
  const [selectedMachine, setSelectedMachine] = useState('');

  const allowedMachineCodes = useMemo(() => {
    if (!Array.isArray(allocatedMachines)) return [];
    return allocatedMachines
      .map((machine) => String(machine.MachineCode || machine.code || '').toUpperCase())
      .filter(Boolean);
  }, [allocatedMachines]);

  /* default: yesterday 07:30 → today 07:30 as dayjs objects */
  const defaultRange = () => {
    const now = dayjs();
    let to    = now.startOf('day').hour(7).minute(30);
    if (now.isBefore(to)) to = to.subtract(1, 'day');
    const from = to.subtract(1, 'day');
    return { from, to };
  };
  const dr = defaultRange();
  const [dateFrom, setDateFrom] = useState(dr.from);
  const [dateTo,   setDateTo]   = useState(dr.to);

  const [expandedId,   setExpandedId]   = useState(null);
  const [groupsData,   setGroupsData]   = useState([]);
  const [generalLogs,  setGeneralLogs]  = useState([]);
  const [timeline,     setTimeline]     = useState([]);
  const [planData,     setPlanData]     = useState([]);
  const [loading,    setLoading]    = useState(false);
  const [fetched,    setFetched]    = useState(false);

  const [snack, setSnack] = useState({ open: false, msg: '', severity: 'info' });
  const onSnackbar = (msg, severity = 'info') => setSnack({ open: true, msg, severity });

  /* load grouped machines
     - HR dashboard mode: load all machine-hour workstations
     - Machine supervisor mode: use only allocated machines passed from MachineSupervisorPage
  */
  useEffect(() => {
    if (restrictedMode && Array.isArray(allocatedMachines)) {
      const grouped = buildGroupsFromAllocatedMachines(allocatedMachines);
      setMachineGroups(grouped);

      if (
        selectedMachine &&
        !allowedMachineCodes.includes(String(selectedMachine).toUpperCase())
      ) {
        setSelectedMachine('');
      }

      return;
    }

    axios.get(`${API_BASE_URL}/api/machines-grouped`)
      .then(res => setMachineGroups(res.data))
      .catch(() => onSnackbar('Could not load machines', 'error'));
  }, [restrictedMode, allocatedMachines, allowedMachineCodes, selectedMachine]);

  /* fetch and group data */
  const handleFetch = async () => {
    if (
      restrictedMode &&
      !allowedMachineCodes.includes(String(selectedMachine).toUpperCase())
    ) {
      onSnackbar('You are not allowed to review this workstation', 'error');
      return;
    }

    setLoading(true);
    setFetched(false);
    try {
      const fmtParam = d => d ? d.format('YYYY-MM-DDTHH:mm') : '';
      const reviewUrls = jobReviewUrls || {
        logs: `${API_BASE_URL}/api/job-review-logs`,
        summary: `${API_BASE_URL}/api/job-review-summary`,
        plan: `${API_BASE_URL}/api/job-plan`,
      };

      const params = {
        machineCode: selectedMachine,
        startTime: fmtParam(dateFrom),
        endTime: fmtParam(dateTo),
      };

      const [logsRes, summaryRes, planRes] = await Promise.all([
        axios.post(reviewUrls.logs, params),
        axios.get(reviewUrls.summary, { params }),
        axios.get(reviewUrls.plan, { params }),
      ]);

      const logs = Array.isArray(logsRes.data)
        ? logsRes.data
        : Array.isArray(logsRes.data?.logs)
          ? logsRes.data.logs
          : [];

      const summaries = Array.isArray(summaryRes.data)
        ? summaryRes.data
        : Array.isArray(summaryRes.data?.summaries)
          ? summaryRes.data.summaries
          : [];

      const plans = Array.isArray(planRes.data)
        ? planRes.data
        : Array.isArray(planRes.data?.plans)
          ? planRes.data.plans
          : [];
      console.log('[job-review] logs:', logs.length, '| summaries:', summaries.length, '| plans:', plans.length);

      /* separate job-related and non-job rows */
      const groupMap    = {};
      const generalRows = [];

      logs.forEach(row => {
        const key = row.JobStartedTime;
        if (!key) {
          generalRows.push(row);
        } else {
          if (!groupMap[key]) groupMap[key] = [];
          groupMap[key].push(row);
        }
      });

      /* build job groups */
      const jobGroups = Object.entries(groupMap).map(([key, rows]) => ({
        type:           'job',
        jobStartedTime: key,
        sortTime:       key,
        opLogs:  rows.sort((a, b) => new Date(a.start_datetime) - new Date(b.start_datetime)),
        summary: summaries.find(s => normalise(s.startDatetime) === normalise(key)) || null,
      }));

      /* build general items — each non-job row is its own timeline entry */
      const generalItems = generalRows.map(row => ({
        type:     'general',
        sortTime: row.start_datetime,
        row,
      }));

      /* merge and sort ascending by time */
      const timeline = [...jobGroups, ...generalItems]
        .sort((a, b) => new Date(a.sortTime) - new Date(b.sortTime));

      setGroupsData(jobGroups.sort((a, b) => new Date(b.jobStartedTime) - new Date(a.jobStartedTime)));
      setGeneralLogs(generalRows.sort((a, b) => new Date(a.start_datetime) - new Date(b.start_datetime)));
      setTimeline(timeline);
      setPlanData(plans);
      setExpandedId(null);
      setFetched(true);
      if (timeline.length === 0) onSnackbar('No job sessions found for this range', 'info');
    } catch (error) {
      onSnackbar(error.response?.data?.error || 'Failed to fetch data', 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
    <Box sx={{ width: '100%', ...touchFriendlyReviewSx }}>

      {/* title */}
      <Stack direction="row" spacing={2} alignItems="center" mb={3}>
        <Box sx={{
          width: 40, height: 40, borderRadius: '10px',
          bgcolor: softPrimaryBg, border: '1px solid', borderColor: alpha(theme.palette.primary.main, 0.35),
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        }}>
          <HowToRegIcon sx={{ fontSize: 20, color: 'primary.main' }} />
        </Box>
        <Box>
          <Typography sx={{ fontSize: 18, fontWeight: 800, color: 'text.primary', lineHeight: 1.2 }}>
            {title}
          </Typography>
          <Typography sx={{ fontSize: 12.5, color: 'text.secondary', mt: 0.2 }}>
            {subtitle}
          </Typography>
        </Box>
      </Stack>

      {/* filter panel */}
      <Paper elevation={0} sx={{
        border: '1px solid', borderRadius: '12px',
        p: 2.5, mb: 3, bgcolor: 'background.paper',
      }}>
        <Typography sx={{ fontSize: 11, fontWeight: 700, color: 'text.secondary',
          textTransform: 'uppercase', letterSpacing: '.5px', mb: 2 }}>
          Select Workstation &amp; Time Range
        </Typography>
        <Grid container spacing={2} alignItems="flex-end">
          <Grid item xs={12} sm={6} md={4}>
            <FormControl fullWidth size="small">
              <InputLabel sx={{ fontSize: 13 }}>Workstation</InputLabel>
              <Select
                value={selectedMachine} label="Workstation"
                onChange={e => setSelectedMachine(e.target.value)}
                sx={{ borderRadius: '8px', fontSize: 13, minHeight: { xs: 48, md: 40 } }}
                MenuProps={{ PaperProps: { sx: { maxHeight: 440, WebkitOverflowScrolling: 'touch' } }, MenuListProps: { sx: { '& .MuiMenuItem-root': { minHeight: 48 } } } }}
              >
                {machineGroups.map(group => [
                  <MyListSubheader
                    key={`grp-${group.groupCode}`}
                    sx={{
                      fontSize: 10.5, fontWeight: 800, color: 'primary.main',
                      textTransform: 'uppercase', letterSpacing: '.6px',
                      bgcolor: softPrimaryBg, lineHeight: '32px',
                    }}
                  >
                    {group.groupDescription}
                  </MyListSubheader>,
                  ...group.machines.map(m => (
                    <MenuItem key={m.code} value={m.code} sx={{ fontSize: 13, pl: 3 }}>
                      <Box>
                        <Typography sx={{ fontSize: 13, lineHeight: 1.2 }}>
                          {m.description}
                        </Typography>
                        <Typography sx={{ fontSize: 10.5, color: 'text.secondary',
                          fontFamily: '"DM Mono", monospace' }}>
                          {m.code}
                        </Typography>
                      </Box>
                    </MenuItem>
                  )),
                ])}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <DateTimePicker
              label="From"
              value={dateFrom}
              onChange={val => setDateFrom(val)}
              slotProps={{
                textField: {
                  fullWidth: true, size: 'small',
                  sx: { '& .MuiOutlinedInput-root': { borderRadius: '8px', fontSize: 13, minHeight: { xs: 48, md: 40 } } },
                },
              }}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <DateTimePicker
              label="To"
              value={dateTo}
              onChange={val => setDateTo(val)}
              slotProps={{
                textField: {
                  fullWidth: true, size: 'small',
                  sx: { '& .MuiOutlinedInput-root': { borderRadius: '8px', fontSize: 13, minHeight: { xs: 48, md: 40 } } },
                },
              }}
            />
          </Grid>

          <Grid item xs={12} md={2}>
            <Button
              fullWidth variant="contained"
              startIcon={loading
                ? <CircularProgress size={14} color="inherit" />
                : <SearchIcon />}
              onClick={handleFetch}
              disabled={!selectedMachine || !dateFrom || !dateTo || !dateFrom.isValid() || !dateTo.isValid() || loading}
              sx={{
                bgcolor: 'primary.main', textTransform: 'none', fontWeight: 700,
                fontSize: 13, borderRadius: '10px', boxShadow: 'none', height: { xs: 48, md: 40 },
                '&:hover': { bgcolor: 'primary.dark', boxShadow: 'none' },
                '&:disabled': { bgcolor: 'action.hover' },
              }}
            >
              {loading ? 'Loading' : 'Load'}
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* ── summary banner ── */}
      {fetched && timeline.length > 0 && (
        <Paper elevation={0} sx={{
          border: '1px solid', borderColor: alpha(theme.palette.primary.main, isDark ? 0.35 : 0.22),
          borderRadius: 2, p: 2, mb: 2.5,
          bgcolor: softPrimaryBg,
          background: isDark
            ? `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.14)} 0%, ${alpha(theme.palette.background.paper, 0.9)} 100%)`
            : `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.07)} 0%, ${alpha(theme.palette.primary.main, 0.02)} 100%)`,
        }}>
          <Stack direction="row" spacing={3} alignItems="center" flexWrap="wrap">
            <Stack direction="row" spacing={1} alignItems="center">
              <WorkOutlineIcon sx={{ fontSize: 16, color: 'primary.main' }} />
              <Typography sx={{ fontSize: 13, fontWeight: 700, color: 'primary.main' }}>
                {selectedMachine}
              </Typography>
            </Stack>
            <Divider orientation="vertical" flexItem />
            {[
              { icon: <AssignmentIcon sx={{ fontSize: 14 }} />, label: 'Job Sessions',       val: groupsData.length,  color: 'primary.main'   },
              { icon: <ScheduleIcon   sx={{ fontSize: 14 }} />, label: 'General Activities', val: generalLogs.length, color: 'text.secondary' },
              { icon: <EventNoteIcon  sx={{ fontSize: 14 }} />, label: 'Planned Jobs',        val: planData.length,    color: 'success.main'   },
            ].map(({ icon, label, val, color }) => (
              <Stack key={label} direction="row" spacing={1} alignItems="center">
                <Box sx={{ color }}>{icon}</Box>
                <Box>
                  <Typography sx={{ fontSize: 18, fontWeight: 800, color, lineHeight: 1, fontFamily: 'monospace' }}>
                    {val}
                  </Typography>
                  <Typography sx={{ fontSize: 10, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '.4px' }}>
                    {label}
                  </Typography>
                </Box>
              </Stack>
            ))}
            <Box flex={1} />
            <Typography sx={{ fontSize: 11, color: 'text.secondary', fontFamily: 'monospace' }}>
              {dateFrom?.format('DD MMM HH:mm')} → {dateTo?.format('DD MMM HH:mm')}
            </Typography>
          </Stack>
        </Paper>
      )}

      {/* no results */}
      {fetched && timeline.length === 0 && (
        <Alert severity="info" sx={{ borderRadius: 2, fontSize: 13 }}>
          No activity found for <strong>{selectedMachine}</strong> in the selected time range.
        </Alert>
      )}

      {/* job plan — grouped by planning slot */}
      {fetched && planData.length > 0 && (() => {
        /* group planData by planningFrom */
        const slotMap = {};
        planData.forEach(p => {
          const key = p.planningFrom;
          if (!slotMap[key]) slotMap[key] = [];
          slotMap[key].push(p);
        });
        const slots = Object.entries(slotMap).sort(([a], [b]) => new Date(a) - new Date(b));
        const fmtSlot = dt => {
          const d = new Date(dt);
          return d.toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
        };
        const TABLE_COLS = [
          { h: 'Seq',            right: true  },
          { h: 'Job No',         pl: true     },
          { h: 'Description'                  },
          { h: 'Customer'                     },
          { h: 'Priority'                     },
          { h: 'WO Qty',         right: true  },
          { h: 'OP Qty',         right: true  },
          { h: 'Planned Qty',    right: true  },
          { h: 'Op Desc'                      },
          { h: 'Color Seq'                    },
          { h: 'Other Details'                },
          { h: 'Setup (hr)',     right: true  },
          { h: 'Op Time (hr)',   right: true  },
          { h: 'Setup+Op (hr)',  right: true  },
          { h: 'Time Unit'                    },
          { h: 'Op Start'                     },
          { h: 'Op End'                       },
          { h: 'WO Due'                       },
          { h: 'WO End'                       },
          { h: 'WO Delay'                     },
          { h: 'Last Updated'                 },
        ];
        return (
          <Box mb={2.5}>
            <Stack direction="row" spacing={0} alignItems="stretch" mb={1.5}>
              <Box sx={{
                width: 4, borderRadius: '4px 0 0 4px',
                bgcolor: 'success.main', flexShrink: 0,
              }} />
              <Box sx={{
                flex: 1, px: 2, py: 1.2,
                bgcolor: softSuccessBg,
                border: '1px solid', borderColor: alpha(theme.palette.success.main, isDark ? 0.35 : 0.22),
                borderLeft: 'none', borderRadius: '0 8px 8px 0',
                display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap',
              }}>
                <AssignmentIcon sx={{ fontSize: 16, color: 'success.main' }} />
                <Typography sx={{ fontSize: 14, fontWeight: 800, color: isDark ? theme.palette.success.light : theme.palette.success.dark, letterSpacing: '-.2px' }}>
                  Job Plan
                </Typography>
                <Chip
                  label={`${planData.length} jobs`}
                  size="small" color="success"
                  sx={{ fontSize: 11, fontWeight: 700, height: 22 }}
                />
                <Chip
                  label={`${slots.length} ${slots.length === 1 ? 'planning slot' : 'planning slots'}`}
                  size="small" variant="outlined" color="success"
                  sx={{ fontSize: 11, fontWeight: 600, height: 22 }}
                />
                <Typography sx={{ fontSize: 11, color: 'text.secondary', ml: 'auto !important' }}>
                  Planned production for selected range
                </Typography>
              </Box>
            </Stack>
            <Stack spacing={1}>
              {slots.map(([slotKey, jobs], slotIdx) => {
                const toDate = jobs[0]?.planingTo;
                const totalPlannedQty = jobs.reduce((s, j) => s + (j.plannedQty || 0), 0);
                const totalOpTime     = jobs.reduce((s, j) => s + (parseFloat(j.OperationalTime) || 0), 0);
                return (
                  <Accordion
                    key={slotKey}
                    defaultExpanded={slotIdx === 0}
                    elevation={0}
                    sx={{
                      border: '1px solid', borderColor: 'divider',
                      borderRadius: '10px !important',
                      '&:before': { display: 'none' },
                      '&.Mui-expanded': { borderColor: 'primary.light' },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      sx={{
                        bgcolor: subtleBg, borderRadius: '10px',
                        '&.Mui-expanded': { bgcolor: softPrimaryBg, borderRadius: '10px 10px 0 0' },
                        minHeight: 48, '& .MuiAccordionSummary-content': { my: 0.8 },
                      }}
                    >
                      <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" width="100%">
                        <Stack direction="row" spacing={1} alignItems="center">
                          <EventNoteIcon sx={{ fontSize: 15, color: 'primary.main' }} />
                          <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: 'primary.main',
                            fontFamily: 'monospace' }}>
                            {fmtSlot(slotKey)}
                          </Typography>
                          <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>→</Typography>
                          <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: 'text.secondary',
                            fontFamily: 'monospace' }}>
                            {toDate ? fmtSlot(toDate) : '—'}
                          </Typography>
                        </Stack>
                        <Stack direction="row" spacing={1} sx={{ ml: 'auto !important', mr: 1 }}>
                          <Chip label={`${jobs.length} jobs`} size="small"
                            sx={{ fontSize: 10.5, height: 20, fontWeight: 600 }} />
                          <Chip label={`${totalPlannedQty.toLocaleString()} planned`} size="small"
                            color="success" variant="outlined"
                            sx={{ fontSize: 10.5, height: 20, fontWeight: 600 }} />
                          <Chip label={`${totalOpTime.toFixed(1)} hrs`} size="small"
                            color="info" variant="outlined"
                            sx={{ fontSize: 10.5, height: 20, fontWeight: 600 }} />
                        </Stack>
                      </Stack>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 0 }}>
                      <TableContainer sx={{ maxHeight: 360, overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                        <Table size="small" stickyHeader sx={{ minWidth: 1200 }}>
                          <TableHead>
                            <TableRow>
                              {TABLE_COLS.map(({ h, pl, right }) => (
                                <TableCell key={h} sx={{
                                  fontSize: 10.5, fontWeight: 700, color: 'text.secondary',
                                  bgcolor: tableHeaderBg, py: 0.8, whiteSpace: 'nowrap',
                                  textTransform: 'uppercase', letterSpacing: '.4px',
                                  ...(right ? { textAlign: 'right' } : {}),
                                  ...(pl    ? { pl: 2 }              : {}),
                                }}>{h}</TableCell>
                              ))}
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {jobs.map((p, i) => (
                              <TableRow key={p.id}
                                sx={{ bgcolor: i % 2 === 0 ? theme.palette.background.paper : subtleBg,
                                      '&:hover': { bgcolor: softPrimaryBg },
                                      transition: 'background .12s' }}>
                                {/* Seq — hover shows full job detail tooltip */}
                                <TableCell sx={{ py: 0.8, fontSize: 12, textAlign: 'right', color: 'text.secondary', pl: 2 }}>
                                  <Tooltip
                                    placement="right"
                                    arrow
                                    title={
                                      <Box sx={{ p: 0.5, minWidth: 240 }}>
                                        <Typography sx={{ fontSize: 12, fontWeight: 700, mb: 1,
                                          fontFamily: 'monospace', color: 'primary.light' }}>
                                          #{p.jobSequenceNumber} — {p.jobNumber}
                                        </Typography>
                                        {[
                                          { label: 'Description',  val: p.WOdescription       },
                                          { label: 'Customer',     val: p.customer             },
                                          { label: 'Priority',     val: p.WOpriority           },
                                          { label: 'Op Desc',      val: p.OpDesctiption        },
                                          { label: 'Color Seq',    val: p.ColorSequence        },
                                          { label: 'Other',        val: p.OtherDetails         },
                                          { label: 'WO Qty',       val: p.WOQty?.toLocaleString()      },
                                          { label: 'OP Qty',       val: p.OPQty?.toLocaleString()      },
                                          { label: 'Planned Qty',  val: p.plannedQty?.toLocaleString() },
                                          { label: 'Setup (hr)',   val: p.SetupTime            },
                                          { label: 'Op Time (hr)', val: p.OperationalTime      },
                                          { label: 'Setup+Op',     val: p.SetupAndOpeTime      },
                                          { label: 'Time Unit',    val: p.TimeUnit             },
                                          { label: 'Op Start',     val: p.opstartDate          },
                                          { label: 'Op End',       val: p.opendDate            },
                                          { label: 'WO Due',       val: p.WOduedate            },
                                          { label: 'WO End',       val: p.WOenddate            },
                                          { label: 'WO Delay',     val: p.WOdelay              },
                                          { label: 'Last Updated', val: p.lastUpdated          },
                                        ].filter(({ val }) => val && val !== '-' && val !== '0').map(({ label, val }) => (
                                          <Box key={label} sx={{ display: 'flex', gap: 1, mb: 0.4 }}>
                                            <Typography sx={{ fontSize: 10.5, color: 'grey.400',
                                              minWidth: 88, flexShrink: 0,
                                              textTransform: 'uppercase', letterSpacing: '.3px' }}>
                                              {label}
                                            </Typography>
                                            <Typography sx={{ fontSize: 10.5, color: 'common.white',
                                              fontWeight: 500, wordBreak: 'break-word' }}>
                                              {val}
                                            </Typography>
                                          </Box>
                                        ))}
                                      </Box>
                                    }
                                    componentsProps={{
                                      tooltip: {
                                        sx: {
                                          bgcolor: 'grey.900',
                                          maxWidth: 320,
                                          border: '1px solid',
                                          borderColor: 'grey.700',
                                          borderRadius: 2,
                                          p: 1.5,
                                          boxShadow: 4,
                                        },
                                      },
                                      arrow: { sx: { color: 'grey.900' } },
                                    }}
                                  >
                                    <Box sx={{
                                      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                      width: 22, height: 22, borderRadius: '50%',
                                      bgcolor: softPrimaryBg, border: '1px solid', borderColor: alpha(theme.palette.primary.main, isDark ? 0.45 : 0.28),
                                      fontSize: 11, fontWeight: 700, color: 'primary.main',
                                      cursor: 'default', userSelect: 'none',
                                      '&:hover': { bgcolor: 'primary.main', color: 'white', borderColor: 'primary.main' },
                                      transition: 'all .15s',
                                    }}>
                                      {p.jobSequenceNumber}
                                    </Box>
                                  </Tooltip>
                                </TableCell>
                                {/* Job No */}
                                <TableCell sx={{ py: 0.8, pl: 2 }}>
                                  <CopyCell value={p.jobNumber} mono color="primary.main" />
                                </TableCell>
                                {/* Description */}
                                <TableCell sx={{ py: 0.8 }}>
                                  <Tooltip title={p.WOdescription || ''} arrow placement="top">
                                    <Box><CopyCell value={p.WOdescription} /></Box>
                                  </Tooltip>
                                </TableCell>
                                {/* Customer */}
                                <TableCell sx={{ py: 0.8 }}>
                                  <CopyCell value={p.customer} color="text.secondary" />
                                </TableCell>
                                {/* Priority */}
                                <TableCell sx={{ py: 0.8 }}>
                                  <Chip label={p.WOpriority || '—'} size="small"
                                    color={p.WOpriority === 'Urgent' ? 'error' : p.WOpriority === 'High' ? 'warning' : 'default'}
                                    sx={{ fontSize: 10.5, height: 18, fontWeight: 600 }} />
                                </TableCell>
                                {/* WO Qty */}
                                <TableCell sx={{ py: 0.8, fontSize: 12, textAlign: 'right', color: 'text.primary' }}>
                                  {p.WOQty?.toLocaleString() ?? '—'}
                                </TableCell>
                                {/* OP Qty */}
                                <TableCell sx={{ py: 0.8, fontSize: 12, textAlign: 'right', color: 'text.primary' }}>
                                  {p.OPQty?.toLocaleString() ?? '—'}
                                </TableCell>
                                {/* Planned Qty */}
                                <TableCell sx={{ py: 0.8, fontSize: 12, textAlign: 'right',
                                  color: 'success.main', fontWeight: 700 }}>
                                  {p.plannedQty?.toLocaleString() ?? '—'}
                                </TableCell>
                                {/* Op Desc */}
                                <TableCell sx={{ py: 0.8 }}>
                                  <Tooltip title={p.OpDesctiption || ''} arrow placement="top">
                                    <Box><CopyCell value={p.OpDesctiption} color="text.secondary" /></Box>
                                  </Tooltip>
                                </TableCell>
                                {/* Color Seq */}
                                <TableCell sx={{ py: 0.8, fontSize: 12, color: 'text.secondary', whiteSpace: 'nowrap' }}>
                                  {p.ColorSequence && p.ColorSequence !== '-' ? p.ColorSequence : '—'}
                                </TableCell>
                                {/* Other Details */}
                                <TableCell sx={{ py: 0.8 }}>
                                  <Tooltip title={p.OtherDetails || ''} arrow placement="top">
                                    <Typography sx={{ fontSize: 12, color: 'text.secondary',
                                      whiteSpace: 'nowrap', overflow: 'hidden',
                                      textOverflow: 'ellipsis', maxWidth: 120 }}>
                                      {p.OtherDetails && p.OtherDetails !== '-' ? p.OtherDetails : '—'}
                                    </Typography>
                                  </Tooltip>
                                </TableCell>
                                {/* Setup (hr) */}
                                <TableCell sx={{ py: 0.8, fontSize: 12, textAlign: 'right', color: 'text.secondary' }}>
                                  {p.SetupTime ?? '—'}
                                </TableCell>
                                {/* Op Time (hr) */}
                                <TableCell sx={{ py: 0.8, fontSize: 12, textAlign: 'right', color: 'text.secondary' }}>
                                  {p.OperationalTime ?? '—'}
                                </TableCell>
                                {/* Setup+Op (hr) */}
                                <TableCell sx={{ py: 0.8, fontSize: 12, textAlign: 'right',
                                  color: 'info.main', fontWeight: 600 }}>
                                  {p.SetupAndOpeTime ?? '—'}
                                </TableCell>
                                {/* Time Unit */}
                                <TableCell sx={{ py: 0.8, fontSize: 12, color: 'text.secondary', whiteSpace: 'nowrap' }}>
                                  {p.TimeUnit || '—'}
                                </TableCell>
                                {/* Op Start */}
                                <TableCell sx={{ py: 0.8, fontSize: 12, color: 'text.secondary', whiteSpace: 'nowrap' }}>
                                  {p.opstartDate || '—'}
                                </TableCell>
                                {/* Op End */}
                                <TableCell sx={{ py: 0.8, fontSize: 12, color: 'text.secondary', whiteSpace: 'nowrap' }}>
                                  {p.opendDate || '—'}
                                </TableCell>
                                {/* WO Due */}
                                <TableCell sx={{ py: 0.8, fontSize: 12, color: 'warning.main', whiteSpace: 'nowrap', fontWeight: 600 }}>
                                  {p.WOduedate || '—'}
                                </TableCell>
                                {/* WO End */}
                                <TableCell sx={{ py: 0.8, fontSize: 12, color: 'text.secondary', whiteSpace: 'nowrap' }}>
                                  {p.WOenddate || '—'}
                                </TableCell>
                                {/* WO Delay */}
                                <TableCell sx={{ py: 0.8 }}>
                                  {p.WOdelay && p.WOdelay !== '0' ? (
                                    <Chip label={p.WOdelay} size="small" color="error" variant="outlined"
                                      sx={{ fontSize: 10.5, height: 18, fontWeight: 600 }} />
                                  ) : (
                                    <Chip label="On time" size="small" color="success" variant="outlined"
                                      sx={{ fontSize: 10.5, height: 18 }} />
                                  )}
                                </TableCell>
                                {/* Last Updated */}
                                <TableCell sx={{ py: 0.8, fontSize: 11, color: 'text.disabled', whiteSpace: 'nowrap' }}>
                                  {p.lastUpdated ? fmt(p.lastUpdated) : '—'}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </AccordionDetails>
                  </Accordion>
                );
              })}
            </Stack>
          </Box>
        );
      })()}

      {/* ── shift timeline ── */}
      {fetched && timeline.length > 0 && (
        <Box>
          {/* timeline section header */}
          <Stack direction="row" spacing={0} alignItems="stretch" mb={2}>
            <Box sx={{
              width: 4, borderRadius: '4px 0 0 4px',
              bgcolor: 'primary.main', flexShrink: 0,
            }} />
            <Box sx={{
              flex: 1, px: 2, py: 1.2,
              bgcolor: softPrimaryBg,
              border: '1px solid', borderColor: alpha(theme.palette.primary.main, isDark ? 0.35 : 0.22),
              borderLeft: 'none', borderRadius: '0 8px 8px 0',
              display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap',
            }}>
              <ScheduleIcon sx={{ fontSize: 16, color: 'primary.main' }} />
              <Typography sx={{ fontSize: 14, fontWeight: 800, color: isDark ? theme.palette.primary.light : theme.palette.primary.dark, letterSpacing: '-.2px' }}>
                Shift Timeline
              </Typography>
              <Chip
                label={`${timeline.filter(t => t.type === 'job').length} jobs`}
                size="small" color="primary"
                sx={{ fontSize: 11, fontWeight: 700, height: 22 }}
              />
              <Chip
                label={`${timeline.filter(t => t.type === 'general').length} general`}
                size="small" variant="outlined" color="default"
                sx={{ fontSize: 11, fontWeight: 600, height: 22 }}
              />
              <Typography sx={{ fontSize: 11, color: 'text.secondary', ml: 'auto !important' }}>
                Chronological order · oldest first
              </Typography>
            </Box>
          </Stack>

          {/* timeline items */}
          <Stack spacing={0}>
            {timeline.map((item, idx) => {
              const isLast = idx === timeline.length - 1;

              if (item.type === 'general') {
                /* ── general activity row ── */
                const row = item.row;
                const ts  = typeStyle(row.operation_type, theme);
                return (
                  <Box key={`gen-${idx}`} sx={{ display: 'flex', alignItems: 'stretch' }}>
                    {/* time label + dot + spine */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center',
                      width: 64, flexShrink: 0, mr: 1.5 }}>
                      <Typography sx={{
                        fontSize: 11, fontWeight: 600, color: 'text.disabled',
                        fontFamily: 'monospace', mt: 0.9, lineHeight: 1,
                      }}>
                        {fmtHHMM(row.start_datetime)}
                      </Typography>
                      <Box sx={{ width: 8, height: 8, borderRadius: '50%', mt: 0.5, flexShrink: 0,
                        bgcolor: alpha(theme.palette.text.secondary, 0.65), border: '2px solid', borderColor: timelineLineBg }} />
                      {!isLast && <Box sx={{ width: 2, flex: 1, bgcolor: timelineLineBg, mt: 0.4 }} />}
                    </Box>
                    {/* general card */}
                    <Box sx={{ flex: 1, mb: isLast ? 0 : 0.75 }}>
                      <Paper elevation={0} sx={{
                        border: '1px solid', borderColor: 'divider',
                        borderRadius: 1.5, px: 2, py: 0.9,
                        bgcolor: subtleBg,
                        display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap',
                      }}>
                        <Chip label={row.operation_type || '—'} size="small"
                          sx={{ bgcolor: ts.bg, color: ts.color, border: `1px solid ${ts.border}`,
                                fontWeight: 600, fontSize: 10.5, height: 20 }} />
                        <Typography sx={{ fontSize: 12.5, color: 'text.primary', fontWeight: 500 }}>
                          {row.operation_description || '—'}
                        </Typography>
                        {row.operation_code && (
                          <Chip label={row.operation_code} size="small" variant="outlined"
                            sx={{ fontSize: 10.5, height: 18 }} />
                        )}
                        <Box sx={{ ml: 'auto !important' }}>
                          <Stack direction="row" spacing={1.5} alignItems="center">
                            <Typography sx={{ fontSize: 11, color: 'text.secondary',
                              fontFamily: 'monospace', whiteSpace: 'nowrap' }}>
                              {fmtHHMM(row.start_datetime)} – {fmtHHMM(row.end_datetime)}
                            </Typography>
                            <Chip label={fmtDur(row.duration_minutes)} size="small"
                              sx={{ fontSize: 10.5, height: 18, bgcolor: subtleBgStrong }} />
                            <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>
                              {row.name || ''}
                            </Typography>
                          </Stack>
                        </Box>
                      </Paper>
                    </Box>
                  </Box>
                );
              }

              /* ── job session card ── */
              const group = item;

              return (
                <Box key={`job-${group.jobStartedTime}`} sx={{ display: 'flex', alignItems: 'stretch' }}>
                  {/* time label + dot + spine */}
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center',
                    width: 64, flexShrink: 0, mr: 1.5 }}>
                    <Typography sx={{
                      fontSize: 11, fontWeight: 700, color: 'primary.main',
                      fontFamily: 'monospace', mt: 1.1, lineHeight: 1,
                    }}>
                      {fmtHHMM(group.jobStartedTime)}
                    </Typography>
                    <Box sx={{ width: 12, height: 12, borderRadius: '50%', mt: 0.5, flexShrink: 0,
                      bgcolor: 'primary.main', border: '2px solid', borderColor: 'primary.light',
                      boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, isDark ? 0.24 : 0.15)}` }} />
                    {!isLast && <Box sx={{ width: 2, flex: 1, bgcolor: alpha(theme.palette.primary.main, isDark ? 0.35 : 0.22), mt: 0.4 }} />}
                  </Box>
                  {/* job card */}
                  <Box sx={{ flex: 1, mb: isLast ? 0 : 1.5 }}>
                    <JobGroup
                      group={group}
                      machineCode={selectedMachine}
                      supervisorId={username}
                      onSnackbar={onSnackbar}
                      expanded={expandedId === group.jobStartedTime}
                      onToggle={() => setExpandedId(
                        expandedId === group.jobStartedTime ? null : group.jobStartedTime
                      )}
                      updateUrl={updateUrl}
                      editSource={editSource}
                    />
                  </Box>
                </Box>
              );
            })}
          </Stack>
        </Box>
      )}

      {/* snackbar */}
      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snack.severity}
          onClose={() => setSnack(s => ({ ...s, open: false }))}
          sx={{ fontSize: 13, borderRadius: '8px' }}
        >
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  </LocalizationProvider>
  );
};

export default MachineHourApproval;